/**
 * EditorialCardGrid — a responsive grid of hairline editorial cards (NO
 * shadow, NO rounded corners — the journal aesthetic). Each card carries
 * an optional small-caps eyebrow, a serif title, an optional large mono
 * score / headline value, an optional "recommended" pick badge, and a
 * definition-list body of key/value rows.
 *
 * Editorial reference: `.pw .card` / `.pcards .pc` / `.spec-cards .sc` in
 *   docs/design/round3-sentiment-risk-pathway.html (Pathway 16B scored cards),
 *   docs/design/round3-recs-feasibility-finance.html (Finance 19B phase cards),
 *   docs/design/round3-conclusion-extended-refs.html (Extended 21B spec cards):
 *     border: 1px solid var(--hair-strong); the winner adds an inset accent
 *     ring; title serif; score mono accent; rows hairline-separated.
 *
 * Used by:
 *   - Pathway / Decision Comparison (16B)  — scored pathway cards (winner ring)
 *   - Phased Buildout & Finance (19B)      — per-phase capex / opex / COD cards
 *   - Extended Site Data (21B)             — spec cards (no score, no pick)
 *
 * Theme tokens read: 'rule' (card border), 'accent' (winner ring + eyebrow +
 * score + pick badge), 'ink' (title), 'body' (row keys), 'muted' (eyebrow on
 * non-winner), 'hair' (row rules). Serif title via inherited
 * --report-body-font is NOT forced (titles use the section's serif); mono
 * faces for eyebrow / score / values via --report-body-font. Pure / print-safe.
 */
import React from 'react';
import { Diamond } from 'lucide-react';
import type { ReportTheme } from '../../../../types/reportTheme';
import { roleColor } from '../../../../lib/reportRoleColors';
import { REPORT_TEXT_PX } from '../../../../lib/reportSectionHeading';

const MONO = 'var(--report-body-font, inherit)';

export interface EditorialCardRow {
  label: React.ReactNode;
  value: React.ReactNode;
}

export interface EditorialCard {
  /** Small-caps mono eyebrow, e.g. "Phase 1". */
  eyebrow?: React.ReactNode;
  /** Card title (serif), e.g. "Hybrid" or "50 MW". */
  title: React.ReactNode;
  /** Large mono headline / score, e.g. 88 or "$420M". */
  score?: React.ReactNode;
  /** Highlight as the recommended option (accent ring + pick badge). */
  recommended?: boolean;
  /** Pick-badge label when `recommended`. Defaults to a vector diamond + "Recommended". */
  pickLabel?: React.ReactNode;
  /** Key/value body rows (hairline-separated). */
  rows?: EditorialCardRow[];
}

export interface EditorialCardGridProps {
  theme: ReportTheme;
  cards: EditorialCard[];
  /** Columns; defaults to the card count (max 3 is the editorial norm). */
  columns?: number;
  id?: string;
  className?: string;
  style?: React.CSSProperties;
}

export function EditorialCardGrid({
  theme,
  cards,
  columns,
  id,
  className,
  style,
}: EditorialCardGridProps): React.ReactElement | null {
  if (cards.length === 0) return null;

  const dense = theme.density === 'compact';
  const border = roleColor(theme, 'rule');
  const accent = roleColor(theme, 'accent');
  const ink = roleColor(theme, 'ink');
  const body = roleColor(theme, 'body');
  const muted = roleColor(theme, 'muted');
  const hair = roleColor(theme, 'hair');
  const cols = columns ?? Math.min(cards.length, 3);

  return (
    <div
      id={id}
      className={className}
      style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
        gap: 14,
        ...style,
      }}
    >
      {cards.map((card, i) => (
        <div
          key={i}
          // N1.5C-flat — open card: the all-sides 1px box + the recommended
          // card's inset accent RING are retired in favour of a single hairline
          // TOP rule per card (a 2px ACCENT rule on the recommended card so it
          // still reads as the pick), so the grid is hairline-separated open
          // columns on the cream page, not boxed cards. This component only ever
          // mounts in editorial mode, so the flag-OFF surface never reaches here.
          style={{
            borderTop: card.recommended ? `2px solid ${accent}` : `1px solid ${border}`,
            paddingTop: dense ? 10 : 12,
            paddingLeft: dense ? 12 : 14,
            paddingRight: dense ? 12 : 14,
            paddingBottom: dense ? 12 : 14,
          }}
        >
          {card.eyebrow != null && (
            <div
              style={{
                fontFamily: MONO,
                fontSize: REPORT_TEXT_PX.micro,
                letterSpacing: '0.14em',
                textTransform: 'uppercase',
                color: card.recommended ? accent : muted,
              }}
            >
              {card.eyebrow}
            </div>
          )}
          <div
            style={{
              fontSize: 17,
              fontWeight: 500,
              color: ink,
              margin: '3px 0 8px',
            }}
          >
            {card.title}
          </div>
          {card.score != null && (
            <div
              style={{
                fontFamily: MONO,
                fontSize: 22,
                fontWeight: 500,
                color: accent,
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {card.score}
            </div>
          )}
          {card.recommended && (
            <div
              style={{
                fontFamily: MONO,
                fontSize: 8.5,
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                color: accent,
                marginTop: 6,
                display: 'flex',
                alignItems: 'center',
                gap: 4,
              }}
            >
              {card.pickLabel ?? (
                <>
                  <Diamond
                    aria-hidden="true"
                    data-pdf-recommended-mark="true"
                    style={{ width: '0.8em', height: '0.8em' }}
                    fill="currentColor"
                    strokeWidth={1.75}
                  />
                  <span>Recommended</span>
                </>
              )}
            </div>
          )}
          {card.rows && card.rows.length > 0 && (
            <dl style={{ margin: card.score != null || card.recommended ? '8px 0 0' : 0 }}>
              {card.rows.map((row, r) => (
                <div
                  key={r}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: 8,
                    fontSize: 11.5,
                    padding: '3px 0',
                    borderBottom: r < card.rows!.length - 1 ? `1px solid ${hair}` : undefined,
                  }}
                >
                  {/* FIX-A — the key/value row is a flex row; without protecting
                      the label it could wrap a short single-word key mid-word
                      ("Headlin/e") when a long value squeezed the flex. Pin the
                      label so it never breaks; let the value absorb the flex and
                      wrap (at word boundaries first) on the right. */}
                  <dt style={{ color: body, flexShrink: 0, whiteSpace: 'nowrap' }}>{row.label}</dt>
                  <dd
                    style={{
                      margin: 0,
                      minWidth: 0,
                      fontFamily: MONO,
                      color: ink,
                      fontVariantNumeric: 'tabular-nums',
                      overflowWrap: 'break-word',
                      wordBreak: 'normal',
                      hyphens: 'none',
                      textAlign: 'right',
                    }}
                  >
                    {row.value}
                  </dd>
                </div>
              ))}
            </dl>
          )}
        </div>
      ))}
    </div>
  );
}
